import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, Tab, Tabs } from '@material-ui/core';
import { FormattedMessage, useIntl } from 'react-intl';
import { Skeleton } from '@material-ui/lab';
import { SearchField } from '~/modules/common/components';
import PaginatedRoleSelectionList from './PaginatedRoleSelectionList';
import useAllRoleOptionsQuery from './useAllRoleOptionsQuery';
import useAssignedRolesQuery from './useAssignedRolesQuery';

export const PAGE_SIZE = 50;

export const getSelectedTabText = ({
  defaultRole,
  selectedCount,
  noDefaultRoleText
}) => {
  const defaultRoleText = defaultRole?.displayText ?? noDefaultRoleText;
  const otherRolesCount = selectedCount - (defaultRole ? 1 : 0);

  return otherRolesCount > 0
    ? `${defaultRoleText} +${otherRolesCount}`
    : defaultRoleText;
};

export const TABS = { SELECTED: 'selected', ALL: 'all' };

const useStyles = makeStyles(theme => ({
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(1)
  },
  tab: {
    minWidth: 0,
    flex: 1,
    fontSize: theme.typography.caption.fontSize
  },
  tabCount: {
    fontWeight: theme.typography.fontWeightBold
  },
  tabLabel: {
    color: theme.palette.text.secondary
  }
}));

const SpecificRolesSelector = ({
  taskUri,
  userUri,
  variant,
  skipAssignedRolesQuery,
  newlySelectedRoles,
  isRoleChecked,
  defaultRole,
  selectedCount,
  onToggle,
  onSetDefault,
  initialActiveTab = TABS.ALL
}) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const [activeTab, setActiveTab] = useState(initialActiveTab);
  const [searchTerm, setSearchTerm] = useState('');
  const [allRolesTabCurrentPage, setAllRolesTabCurrentPage] = useState(1);
  const [
    selectedRolesTabCurrentPage,
    setSelectedRolesTabCurrentPage
  ] = useState(1);

  const handleTabChange = useCallback((_, newTab) => {
    setActiveTab(newTab);
    setAllRolesTabCurrentPage(1);
    setSelectedRolesTabCurrentPage(1);
  }, []);

  const handleSearchChange = useCallback(e => {
    setSearchTerm(e.target.value);
    setAllRolesTabCurrentPage(1);
    setSelectedRolesTabCurrentPage(1);
  }, []);

  const isAllTabActive = activeTab === TABS.ALL;

  const allRolesResult = useAllRoleOptionsQuery({
    variant,
    taskUri,
    userUri,
    page: allRolesTabCurrentPage,
    pageSize: PAGE_SIZE,
    textSearch: searchTerm
  });

  const assignedRolesResult = useAssignedRolesQuery({
    variant,
    skipAssignedRolesQuery,
    taskUri,
    userUri,
    page: selectedRolesTabCurrentPage,
    pageSize: PAGE_SIZE,
    textSearch: searchTerm
  });

  const { roles, loading } = isAllTabActive
    ? allRolesResult
    : assignedRolesResult;

  const selectedRoles = useMemo(() => {
    if (isAllTabActive) return [];

    const search = searchTerm.trim().toLowerCase();
    const newlySelectedRolesToShow = newlySelectedRoles.filter(r =>
      r.displayText.toLowerCase().includes(search)
    );

    return [...assignedRolesResult.roles, ...newlySelectedRolesToShow];
  }, [
    isAllTabActive,
    assignedRolesResult.roles,
    newlySelectedRoles,
    searchTerm
  ]);

  const selectedTabText = getSelectedTabText({
    defaultRole,
    selectedCount,
    noDefaultRoleText: formatMessage({
      id: 'selectTaskRolesDialog.noDefaultRole'
    })
  });

  const selectedTabLabel = useMemo(
    () => (
      <span>
        <span className={classes.tabCount}>{selectedTabText}</span>
        <br />
        <span className={classes.tabLabel}>
          <FormattedMessage id="selectTaskRolesDialog.selectedRoles" />
        </span>
      </span>
    ),
    [classes.tabCount, classes.tabLabel, selectedTabText]
  );

  const allTabLabel = useMemo(
    () => (
      <span>
        <span className={classes.tabCount} />
        <br />
        <span className={classes.tabLabel}>
          <FormattedMessage id="selectTaskRolesDialog.allRoles" />
        </span>
      </span>
    ),
    [classes.tabCount, classes.tabLabel]
  );

  return (
    <>
      <SearchField
        value={searchTerm}
        onChange={handleSearchChange}
        placeholder={formatMessage({ id: 'selectTaskRolesDialog.search' })}
      />

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        className={classes.tabs}
        indicatorColor="primary"
      >
        <Tab
          value={TABS.SELECTED}
          className={classes.tab}
          label={selectedTabLabel}
        />
        <Tab value={TABS.ALL} className={classes.tab} label={allTabLabel} />
      </Tabs>

      {loading ? (
        <>
          <Skeleton height={36} />
          <Skeleton height={36} />
          <Skeleton height={36} />
        </>
      ) : (
        <>
          {isAllTabActive && (
            <PaginatedRoleSelectionList
              rows={roles}
              recordCount={roles.length}
              currentPage={allRolesTabCurrentPage}
              setCurrentPage={setAllRolesTabCurrentPage}
              pageSize={PAGE_SIZE}
              isRoleChecked={isRoleChecked}
              defaultRole={defaultRole}
              onToggle={onToggle}
              onSetDefault={onSetDefault}
            />
          )}

          {!isAllTabActive && (
            <PaginatedRoleSelectionList
              rows={selectedRoles}
              recordCount={assignedRolesResult.roles.length}
              currentPage={selectedRolesTabCurrentPage}
              setCurrentPage={setSelectedRolesTabCurrentPage}
              pageSize={PAGE_SIZE}
              isRoleChecked={isRoleChecked}
              defaultRole={defaultRole}
              onToggle={onToggle}
              onSetDefault={onSetDefault}
              emptyMessageId="selectTaskRolesDialog.noSelectedRoles"
            />
          )}
        </>
      )}
    </>
  );
};

SpecificRolesSelector.propTypes = {
  taskUri: PropTypes.string,
  userUri: PropTypes.string,
  variant: PropTypes.oneOf(['taskRole', 'taskResourceRole']).isRequired,
  skipAssignedRolesQuery: PropTypes.bool,
  newlySelectedRoles: PropTypes.arrayOf(
    PropTypes.shape({
      roleUri: PropTypes.string.isRequired,
      displayText: PropTypes.string.isRequired
    })
  ).isRequired,
  isRoleChecked: PropTypes.func.isRequired,
  defaultRole: PropTypes.shape({
    id: PropTypes.string,
    displayText: PropTypes.string
  }),
  selectedCount: PropTypes.number.isRequired,
  onToggle: PropTypes.func.isRequired,
  onSetDefault: PropTypes.func.isRequired,
  initialActiveTab: PropTypes.oneOf(['selected', 'all'])
};

export default SpecificRolesSelector;
